import axios from 'axios';

import getAuthSession from './auth';
import { WORKFLOW_STATUSES } from './workflow';

export interface AutofillUnansweredQuestion {
  shortCode: string;
  bestPractice: string;
}

export interface AutofillSkippedQuestion {
  shortCode: string;
  reason: string;
}

export interface AutofillResult {
  answeredCount: number;
  leftUnanswered: AutofillUnansweredQuestion[];
  skipped: AutofillSkippedQuestion[];
  scopeStable: boolean;
}

// Mirrors the API's refusal set.
export const UNFILLABLE_SURVEY_STATUSES: string[] = [
  WORKFLOW_STATUSES.COMPLETE,
  WORKFLOW_STATUSES.SUBMITTED_FOR_ASSESSMENT,
];

export const AUTOFILL_MODES = {
  QUESTIONNAIRE: 'questionnaire',
  ASSESSOR: 'assessor',
} as const;

export type AutofillMode = (typeof AUTOFILL_MODES)[keyof typeof AUTOFILL_MODES];

const AUTOFILL_PATHS: Record<AutofillMode, string> = {
  [AUTOFILL_MODES.QUESTIONNAIRE]: 'autofill-questionnaire-answers',
  [AUTOFILL_MODES.ASSESSOR]: 'autofill-assessor-answers',
};

// Internal testing tool, flag-gated on the API as well as in the UI.
export async function autofillAnswers(
  mode: AutofillMode,
  workflowId: number,
  leaveUnanswered: number = 1,
): Promise<AutofillResult> {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/${AUTOFILL_PATHS[mode]}/${workflowId}/`,
    { leaveUnanswered },
    {
      headers: {
        'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
    },
  );

  return response.data as AutofillResult;
}

// DRF sends PermissionDenied as { detail } and a bare ValidationError as a string array.
export function autofillErrorMessage(error: unknown): string {
  const fallback = 'The autofill failed.';

  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data?.detail === 'string') return data.detail;
    if (Array.isArray(data) && typeof data[0] === 'string') return data[0];

    return error.message || fallback;
  }

  return error instanceof Error ? error.message : fallback;
}

export default autofillAnswers;
